import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { bairros, SERVICE_LABELS } from "@/data/bairros";
import { bairrosRio } from "@/data/bairrosRio";
import { useCity } from "@/contexts/CityContext";

const routeNames: Record<string, string> = {
  "": "Home",
  "diferencial": "Diferencial USA",
  "quem-somos": "Quem Somos",
  "contato": "Contato",
  "servicos": "Serviços",
  "brasilia": "Brasília",
  "rio-de-janeiro": "Rio de Janeiro",
  "blog": "Blog",
};

/** Resolve a slug into a human-readable name based on route context. */
function resolveSegmentName(segment: string, segments: string[], index: number): string {
  // Fixed routes
  if (routeNames[segment]) return routeNames[segment];

  // Service slug (last segment after a bairro, or under /servicos)
  const svcLabel = SERVICE_LABELS[segment]?.label;
  if (svcLabel) return svcLabel;

  // Bairro slug — check which cluster we're in
  const cluster = segments[0];
  if (cluster === "rio-de-janeiro") {
    const found = bairrosRio.find((b) => b.slug === segment);
    if (found) return found.name;
  }
  if (cluster === "brasilia") {
    const found = bairros.find((b) => b.slug === segment);
    if (found) return found.name;
  }

  // Fallback: humanize slug
  return humanizeSlug(segment);
}

function humanizeSlug(slug: string): string {
  return slug
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const Breadcrumbs = () => {
  const location = useLocation();
  const { isRio } = useCity();
  const segments = location.pathname.split("/").filter(Boolean);

  if (segments.length === 0) return null;

  // For Rio routes, skip "rio-de-janeiro" as first breadcrumb since Home already represents it
  const isRioRoute = segments[0] === "rio-de-janeiro";
  const displaySegments = isRioRoute ? segments.slice(1) : segments;
  const homeLink = isRio ? "/rio-de-janeiro" : "/";

  return (
    <nav aria-label="Breadcrumb" className="bg-muted/50 border-b">
      <div className="container mx-auto px-4 py-2.5">
        <ol className="flex items-center gap-1.5 text-sm text-muted-foreground flex-wrap">
          <li className="flex items-center gap-1.5">
            <Link to={homeLink} className="hover:text-primary transition-colors flex items-center gap-1">
              <Home className="w-3.5 h-3.5" />
              <span>Home</span>
            </Link>
          </li>
          {displaySegments.map((segment, i) => {
            const pathSegments = isRioRoute
              ? ["rio-de-janeiro", ...displaySegments.slice(0, i + 1)]
              : segments.slice(0, i + 1);
            const path = "/" + pathSegments.join("/");
            const isLast = i === displaySegments.length - 1;
            const name = resolveSegmentName(segment, segments, isRioRoute ? i + 1 : i);
            return (
              <li key={path} className="flex items-center gap-1.5">
                <ChevronRight className="w-3.5 h-3.5 opacity-40" />
                {isLast ? (
                  <span className="font-medium text-foreground">{name}</span>
                ) : (
                  <Link to={path} className="hover:text-primary transition-colors">
                    <span>{name}</span>
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumbs;
